/**
 * The pause menu's DOM (plan G12). Renders the pure model from `src/hud/menu.ts` into the modal overlay layer
 * and turns mouse and pointer input into model calls through `MenuViewHandlers`; keyboard and gamepad input
 * are routed by the feature. Built on every open (other overlays reuse `#overlay`), re-rendered on a screen
 * change, and synced in place (values, focus) otherwise. UI events only, never per frame.
 */
import type { Settings } from '../../app/feature'
import type { ControlsGroup } from '../../hud/controlsRef'
import {
  currentItems,
  formatItemValue,
  SCREEN_TITLES,
  selectOptionLabel,
  selectValue,
  sliderFraction,
  type MenuItem,
  type MenuModel,
} from '../../hud/menu'
import { GAME_TITLE } from '../../ui/config'
import { createNoticeTimer } from './notice'
import { slotActionHint } from './saves'

export interface MenuViewHandlers {
  /** The pointer moved over item `index` of the current screen. */
  focus(index: number): void
  /** Click on item `index` (buttons and toggles). Runs inside the click gesture. */
  activate(index: number): void
  /** A slider was dragged to `value`. */
  slide(index: number, value: number): void
  /** A select's segment (quality, aim assist, the Interface selects) was clicked: item `index` takes `value`. */
  select(index: number, value: string): void
  /** A save slot row's action button `action` was clicked. Runs inside the click gesture. */
  slotAction(index: number, action: number): void
}

/** Help pages and the HUD legend (`help.json`). */
export interface HelpContent {
  readonly pages: readonly { readonly id: string; readonly title: string; readonly paragraphs: readonly string[] }[]
  readonly legend: {
    readonly id: string
    readonly title: string
    readonly caption: string
    readonly regions: readonly { readonly label: string; readonly x: number; readonly y: number; readonly w: number; readonly h: number }[]
  }
}

export interface MenuView {
  /** Builds the menu into the overlay layer and shows it. */
  show(reason: string): void
  /** Rebuilds the current screen (after a screen change). */
  render(): void
  /** Updates values and the focus ring in place. */
  sync(): void
  /** Scrolls the controls reference by one step (-1 up, 1 down). */
  scroll(dir: -1 | 1): void
  /** Shows a transient line under the list (e.g. why an action did nothing). */
  notice(text: string): void
  /** A standing line under the screen title (the saves banner, "Last save 2 min ago"); '' clears it. */
  note(text: string): void
  /** Hides the menu while a modal screen is open over it (no bleed-through), and shows it again. */
  cover(active: boolean): void
  /** True while the menu's DOM is what the overlay layer shows. */
  readonly visible: boolean
  hide(): void
}

const SVG_NS = 'http://www.w3.org/2000/svg'
const SCROLL_STEP_PX = 56
/** How long a notice stays up once it has been painted (see `./notice`). */
const NOTICE_MS = 6000
/** Last pointer position that moved the focus. */
const lastPointer = { x: Number.NaN, y: Number.NaN }

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (className) e.className = className
  if (text !== undefined) e.textContent = text
  return e
}

/** The hint line for the focused item: a save slot describes its focused action (W1 polish), the rest their hint. */
function focusHint(item: MenuItem, slotAction: number): string {
  if (item.kind !== 'slot') return item.hint
  const a = item.actions[slotAction]
  return a === undefined ? item.hint : slotActionHint(a)
}

function keycaps(parent: HTMLElement, label: string): void {
  // "Esc / P" → two keycaps with a separator; free text such as "(hold)" stays plain.
  const parts = label.split(' / ')
  parts.forEach((part, i) => {
    if (i > 0) parent.append(el('span', 'ecl-sep', '/'))
    const m = /^(.*?)( \(.+\))$/.exec(part)
    parent.append(el('kbd', 'ecl-cap', m ? m[1] : part))
    if (m) parent.append(el('span', 'ecl-note', m[2].trim()))
  })
}

export function createMenuView(
  root: HTMLElement,
  model: MenuModel,
  settings: () => Settings,
  controls: readonly ControlsGroup[],
  help: HelpContent,
  on: MenuViewHandlers,
): MenuView {
  let panel: HTMLElement | null = null
  let list: HTMLElement | null = null
  let screenTitle: HTMLElement | null = null
  let hintEl: HTMLElement | null = null
  let noticeEl: HTMLElement | null = null
  let noteEl: HTMLElement | null = null
  let covered = false
  let scroller: HTMLElement | null = null
  let rows: HTMLElement[] = []
  const noticeTimer = createNoticeTimer(NOTICE_MS)

  const isVisible = (): boolean => panel !== null && !root.hidden && root.contains(panel)

  function itemRow(item: MenuItem, index: number): HTMLElement {
    const row = el('div', `ecl-item ecl-item--${item.kind}`)
    row.dataset.index = String(index)
    row.dataset.id = item.id
    row.tabIndex = -1
    // Real pointer movement only: rows rebuilt under a resting cursor must not steal the keyboard focus.
    row.addEventListener('pointermove', (e) => {
      if (e.clientX === lastPointer.x && e.clientY === lastPointer.y) return
      lastPointer.x = e.clientX
      lastPointer.y = e.clientY
      on.focus(index)
    })
    const label = el('span', 'ecl-item__label', item.label)
    if (item.kind !== 'slot') row.append(label)
    switch (item.kind) {
      case 'button': {
        row.setAttribute('role', 'button')
        row.append(el('span', 'ecl-item__chevron'))
        row.addEventListener('click', () => on.activate(index))
        break
      }
      case 'toggle': {
        row.setAttribute('role', 'switch')
        const sw = el('span', 'ecl-switch')
        sw.append(el('span', 'ecl-switch__knob'))
        row.append(el('span', 'ecl-item__value'), sw)
        row.addEventListener('click', () => on.activate(index))
        break
      }
      case 'slider': {
        row.setAttribute('role', 'group')
        const input = el('input', 'ecl-range')
        input.type = 'range'
        input.min = String(item.range.min)
        input.max = String(item.range.max)
        input.step = String(item.range.step)
        input.tabIndex = -1
        input.setAttribute('aria-label', item.label)
        input.addEventListener('input', () => on.slide(index, Number(input.value)))
        row.append(el('span', 'ecl-item__value'), input)
        break
      }
      case 'slot': {
        row.setAttribute('role', 'group')
        const text = el('span', 'ecl-slot__text')
        text.append(label, el('span', 'ecl-slot__summary', item.hint))
        row.append(text)
        const acts = el('span', 'ecl-slot__actions')
        item.actions.forEach((a, ai) => {
          const b = el('button', 'ecl-slot__act', a.label)
          b.type = 'button'
          b.tabIndex = -1
          b.dataset.action = a.id
          b.toggleAttribute('data-disabled', !a.enabled)
          if (a.reason !== null) b.title = a.reason
          b.addEventListener('click', () => on.slotAction(index, ai))
          acts.append(b)
        })
        row.append(acts)
        break
      }
      case 'select': {
        row.setAttribute('role', 'radiogroup')
        const seg = el('span', 'ecl-seg')
        const add = (value: string, label: string, click: () => void): void => {
          const b = el('button', 'ecl-seg__opt', label)
          b.type = 'button'
          b.tabIndex = -1
          b.dataset.preset = value
          b.setAttribute('role', 'radio')
          b.addEventListener('click', click)
          seg.append(b)
        }
        for (const value of item.options) add(value, selectOptionLabel(item.id, value), () => on.select(index, value))
        row.append(seg)
        break
      }
    }
    return row
  }

  function controlsTable(): HTMLElement {
    const wrap = el('div', 'ecl-controls')
    const table = el('table', 'ecl-controls__table')
    const head = el('tr')
    head.append(el('th', undefined, 'Action'), el('th', undefined, 'Keyboard + mouse'), el('th', undefined, 'Gamepad'))
    const thead = el('thead')
    thead.append(head)
    const tbody = el('tbody')
    for (const g of controls) {
      const gr = el('tr', 'ecl-controls__group')
      gr.dataset.group = g.id
      const th = el('th', undefined, g.title)
      th.colSpan = 3
      gr.append(th)
      tbody.append(gr)
      for (const r of g.rows) {
        const tr = el('tr')
        const kb = el('td')
        const gp = el('td')
        keycaps(kb, r.keyboard)
        keycaps(gp, r.pad)
        tr.append(el('td', 'ecl-controls__action', r.action), kb, gp)
        tbody.append(tr)
      }
    }
    table.append(thead, tbody)
    wrap.append(table)
    return wrap
  }

  function svgEl(tag: 'rect' | 'text', attrs: Readonly<Record<string, string>>, className: string): SVGElement {
    const e = document.createElementNS(SVG_NS, tag)
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v)
    e.setAttribute('class', className)
    return e
  }

  function legendSvg(): SVGSVGElement {
    const svg = document.createElementNS(SVG_NS, 'svg')
    svg.setAttribute('viewBox', '0 0 100 56')
    svg.setAttribute('class', 'ecl-legend-svg')
    svg.setAttribute('role', 'img')
    svg.setAttribute('aria-label', help.legend.caption)
    svg.append(svgEl('rect', { x: '0.5', y: '0.5', width: '99', height: '55' }, 'ecl-legend-svg__frame'))
    help.legend.regions.forEach((r, i) => {
      const y = r.y * 0.56
      const h = r.h * 0.56
      svg.append(svgEl('rect', { x: String(r.x), y: String(y), width: String(r.w), height: String(h) }, 'ecl-legend-svg__region'))
      const num = svgEl('text', { x: String(r.x + r.w / 2), y: String(y + h / 2 + 1.5) }, 'ecl-legend-svg__num')
      num.textContent = String(i + 1)
      svg.append(num)
    })
    return svg
  }

  function helpBody(): HTMLElement {
    const wrap = el('div', 'ecl-help')
    wrap.dataset.page = model.helpPage
    if (model.helpPage === help.legend.id) {
      wrap.append(el('h3', 'ecl-help__title', help.legend.title), legendSvg())
      const ol = el('ol', 'ecl-help__legend')
      for (const r of help.legend.regions) ol.append(el('li', undefined, r.label))
      wrap.append(ol)
      return wrap
    }
    const page = help.pages.find((pg) => pg.id === model.helpPage)
    if (page) {
      wrap.append(el('h3', 'ecl-help__title', page.title))
      for (const para of page.paragraphs) wrap.append(el('p', undefined, para))
    }
    return wrap
  }

  function updateCue(): void {
    if (!list) return
    list.toggleAttribute('data-more', list.scrollTop + list.clientHeight < list.scrollHeight - 2)
    // The controls table and help pages scroll inside their own box: same fade-and-chevron cue (W1 polish).
    if (scroller) scroller.toggleAttribute('data-more', scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 2)
  }

  function render(): void {
    if (!panel || !list || !screenTitle) return
    const target = list
    panel.dataset.screen = model.screen
    screenTitle.textContent = SCREEN_TITLES[model.screen]
    target.replaceChildren()
    scroller = null
    if (model.screen === 'controls') {
      const table = controlsTable()
      scroller = table
      table.addEventListener('scroll', updateCue, { passive: true })
      target.append(table)
    } else if (model.screen === 'helpPage') {
      const body = helpBody()
      scroller = body
      body.addEventListener('scroll', updateCue, { passive: true })
      target.append(body)
    }
    const items = currentItems(model)
    rows = items.map(itemRow)
    let group: string | undefined
    rows.forEach((row, i) => {
      const item = items[i]
      const g = item.kind === 'slot' ? undefined : item.group
      if (g !== undefined && g !== group) target.append(el('div', 'ecl-group', g))
      group = g
      target.append(row)
    })
    // Replay the entrance animation for the new screen.
    list.classList.remove('ecl-enter')
    void list.offsetWidth
    list.classList.add('ecl-enter')
    sync()
  }

  function sync(): void {
    const s = settings()
    const items = currentItems(model)
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const item = items[i]
      const focused = i === model.focus
      row.toggleAttribute('data-focused', focused)
      const value = row.querySelector<HTMLElement>('.ecl-item__value')
      if (value) value.textContent = formatItemValue(item, s)
      if (item.kind === 'toggle') {
        row.setAttribute('aria-checked', String(s[item.id]))
        row.dataset.on = s[item.id] ? '1' : '0'
      } else if (item.kind === 'slider') {
        const input = row.querySelector<HTMLInputElement>('input')
        if (input && Number(input.value) !== s[item.id]) input.value = String(s[item.id])
        row.style.setProperty('--fill', sliderFraction(item, s).toFixed(4))
      } else if (item.kind === 'slot') {
        row.querySelectorAll<HTMLElement>('.ecl-slot__act').forEach((b, ai) => b.toggleAttribute('data-selected', focused && ai === model.slotAction))
      } else if (item.kind === 'select') {
        for (const b of row.querySelectorAll<HTMLElement>('.ecl-seg__opt')) {
          const selected = b.dataset.preset === selectValue(item.id, s)
          b.toggleAttribute('data-selected', selected)
          b.setAttribute('aria-checked', String(selected))
        }
      }
      if (focused) {
        if (document.activeElement !== row) row.focus({ preventScroll: true })
        row.scrollIntoView({ block: 'nearest' })
        if (hintEl) hintEl.textContent = focusHint(item, model.slotAction)
      }
    }
    updateCue()
  }

  return {
    get visible(): boolean {
      return isVisible()
    },
    show(reason: string): void {
      panel = el('div', 'ecl-menu')
      panel.setAttribute('role', 'dialog')
      panel.setAttribute('aria-modal', 'true')
      panel.setAttribute('aria-labelledby', 'ecl-menu-screen')
      const header = el('header', 'ecl-menu__head')
      const brand = el('div', 'ecl-menu__brand', GAME_TITLE)
      const status = el('div', 'ecl-menu__status')
      status.append(el('span', 'ecl-menu__dot'), el('span', 'ecl-menu__reason', reason))
      header.append(brand, status)
      screenTitle = el('h2', 'ecl-menu__screen')
      screenTitle.id = 'ecl-menu-screen'
      list = el('div', 'ecl-menu__list')
      list.addEventListener('scroll', updateCue, { passive: true })
      noteEl = el('p', 'ecl-menu__note')
      hintEl = el('p', 'ecl-menu__hint')
      noticeEl = el('p', 'ecl-menu__notice')
      noticeEl.setAttribute('role', 'status')
      const legend = el('footer', 'ecl-menu__legend')
      const kb = el('span', 'ecl-legend')
      keycaps(kb, '↑ / ↓')
      kb.append(el('span', 'ecl-note', 'select'))
      keycaps(kb, '← / →')
      kb.append(el('span', 'ecl-note', 'adjust'))
      keycaps(kb, 'Enter')
      kb.append(el('span', 'ecl-note', 'confirm'))
      keycaps(kb, 'Esc')
      kb.append(el('span', 'ecl-note', 'back'))
      const pad = el('span', 'ecl-legend')
      keycaps(pad, 'A')
      pad.append(el('span', 'ecl-note', 'select'))
      keycaps(pad, 'B')
      pad.append(el('span', 'ecl-note', 'back'))
      keycaps(pad, 'Start')
      pad.append(el('span', 'ecl-note', 'resume'))
      legend.append(kb, pad)
      panel.append(header, screenTitle, noteEl, list, hintEl, noticeEl, legend)
      panel.toggleAttribute('data-covered', covered)
      root.replaceChildren(panel)
      root.hidden = false
      render()
    },
    render,
    sync,
    scroll(dir: -1 | 1): void {
      scroller?.scrollBy({ top: dir * SCROLL_STEP_PX, behavior: 'smooth' })
    },
    notice(text: string): void {
      if (!noticeEl) return
      noticeEl.textContent = text
      noticeTimer.arm(() => {
        if (noticeEl) noticeEl.textContent = ''
      })
    },
    note(text: string): void {
      if (noteEl) noteEl.textContent = text
    },
    cover(active: boolean): void {
      covered = active
      panel?.toggleAttribute('data-covered', active)
    },
    hide(): void {
      if (isVisible()) root.hidden = true
      panel = null
      list = null
      rows = []
    },
  }
}
